import { MAX_FILES_PER_UPLOAD } from '@vizit/constants';

/** Thrown by `onAddFiles` when the files would take the queue past `MAX_FILES_PER_UPLOAD` files still to upload. */
export class UploadQueueFullError extends Error {
  /** How many files were turned down; none of them were added. */
  readonly fileCount: number;
  /** How many more files the queue takes right now, while the files already in it upload. */
  readonly roomLeft: number;

  constructor(fileCount: number, roomLeft: number) {
    super(`Cannot add ${fileCount} files. Only ${roomLeft} more fit in the upload queue.`);
    this.name = 'UploadQueueFullError';
    this.fileCount = fileCount;
    this.roomLeft = roomLeft;
  }
}

const files = (count: number) => (count === 1 ? 'file' : 'files');

/**
 * The toast copy for a failed `onAddFiles`: that none of the files were added, why, and, when the queue is busy, how
 * many more fit right now.
 */
export function describeUploadQueueError(error: unknown): { title: string; description: string } {
  if (!(error instanceof UploadQueueFullError)) {
    return { title: "Couldn't start the upload", description: 'Something went wrong. Please try again.' };
  }
  const notAdded =
    error.fileCount === 1 ? "The file wasn't added." : `None of the ${error.fileCount} files were added.`;
  if (error.roomLeft >= MAX_FILES_PER_UPLOAD) {
    return {
      title: 'Too many files',
      description: `${notAdded} You can upload up to ${MAX_FILES_PER_UPLOAD} files at a time.`,
    };
  }
  if (error.roomLeft === 0) {
    return {
      title: 'Too many files uploading',
      description: `${notAdded} Up to ${MAX_FILES_PER_UPLOAD} files can upload at a time, so wait for this upload to finish, then add more.`,
    };
  }
  return {
    title: 'Too many files uploading',
    description: `${notAdded} Up to ${MAX_FILES_PER_UPLOAD} files can upload at a time, so you can add ${error.roomLeft} more ${files(error.roomLeft)} while this upload finishes.`,
  };
}
